import { forwardRef, useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";

// Tests drive clicks synchronously; in the browser we yield one frame first so
// the spinner is painted before a heavy editor re-render blocks the thread.
const defersActions = process.env.NODE_ENV !== "test";

const afterPaint = (callback) => {
  if (!defersActions || typeof window.requestAnimationFrame !== "function") {
    callback();
    return;
  }
  window.requestAnimationFrame(() => window.setTimeout(callback, 0));
};

// Wrap a button so each click shows a spinner until the action (sync or async)
// has finished and the resulting render has been committed.
// placement "center" hides the label under the spinner (compact action buttons);
// "end" keeps the label readable, dims it and spins at the right edge (list rows).
export default function withPendingFeedback(Button, { placement = "center" } = {}) {
  const PendingButton = forwardRef(function PendingButton({ onClick, children, style, ...props }, ref) {
    const [pending, setPending] = useState(false);
    // Only the frame between the click and the handler is guarded against
    // double clicks. Once the handler runs, the page's own guards decide; an
    // older async action that is still settling must not swallow a new click.
    const starting = useRef(false);
    const running = useRef(0);
    const mounted = useRef(true);
    useEffect(() => () => { mounted.current = false; }, []);

    const finish = () => afterPaint(() => {
      running.current = Math.max(0, running.current - 1);
      if (mounted.current && running.current === 0) setPending(false);
    });
    const handleClick = (event) => {
      // Buttons inside clickable cards must not let the click reach the card,
      // and the deferred handler would be too late to stop it.
      event.stopPropagation();
      if (starting.current || !onClick) return;
      starting.current = true;
      running.current += 1;
      if (defersActions) setPending(true);
      afterPaint(() => {
        starting.current = false;
        let result;
        try {
          result = onClick(event);
        } catch (error) {
          finish();
          throw error;
        }
        if (result && typeof result.then === "function") result.finally(finish);
        else finish();
      });
    };

    return (
      <Button ref={ref} {...props}
        style={{ position: "relative", ...(placement === "end" && pending ? { opacity: .6, paddingRight: 26 } : {}), ...style }}
        aria-busy={pending || undefined} data-pending={pending || undefined} onClick={handleClick}>
        {placement === "end" ? children : <PendingContent $pending={pending}>{children}</PendingContent>}
        {pending && <PendingSpinner aria-hidden="true" $placement={placement} />}
      </Button>
    );
  });
  PendingButton.displayName = `withPendingFeedback(${Button.displayName || Button.name || "Button"})`;
  return PendingButton;
}

const spin = keyframes`to { transform: rotate(360deg); }`;

// display: contents keeps the button's own flex gap between icon and label.
// A custom tag, not a <span>, so host styles and queries for spans are unaffected.
const PendingContent = styled("pending-content")`
  display: contents;
  visibility: ${({ $pending }) => ($pending ? "hidden" : "visible")};
`;

// An <i>, not a <span>: host buttons may style every descendant span.
const PendingSpinner = styled.i`
  position: absolute;
  top: 50%;
  ${({ $placement }) => ($placement === "end" ? "right: 6px;" : "left: 50%;")}
  width: 14px;
  height: 14px;
  margin: ${({ $placement }) => ($placement === "end" ? "-9px 0 0 0" : "-9px 0 0 -9px")};
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 999px;
  opacity: .75;
  animation: ${spin} .7s linear infinite;
  will-change: transform;
  pointer-events: none;
`;
